import useApi from '../../../api/useApi'
import ConfigurationContext from '../../../core/providers/ConfigurationContext'
import { useContext } from 'react'
import { VideoSession } from '../../video/VideoSession'
import { useBaseProcessor } from '../useBaseProcessor'
import { type UploadFormat } from '../UploadFormat'
import { useTrackSessionContext } from '../../trackSession/TrackSessionContext'
import { type Device } from '@pitbrain/shared'
import { extract } from './extracts/gpmf-extract'
import { parseGoProToRawTelemetry } from './parseGoProTelemetry'
import { rememberRawTelemetry } from '../rawTelemetrySource'

export const useGoProProcessor = (
  setProgressPercent: React.Dispatch<React.SetStateAction<number>>
): UploadFormat => {
  const bffConfig = useContext(ConfigurationContext)
  const api = useApi(bffConfig)
  const { setVideoSessions } = useTrackSessionContext()
  const { getFirstCoordinate, processAndCreateSession } = useBaseProcessor()
  const { error: apiError } = api

  const addAsVideo = (sessionId: string, file: File): void => {
    setVideoSessions((prevState) => [
      ...prevState,
      new VideoSession(sessionId, file),
    ])
  }

  const processFile = async (file: File, sessionId: string): Promise<void> => {
    const progress = (percent: number) => {
      setProgressPercent(percent)
    }

    const gpmfTrack = await extract(file, { progress })

    rememberRawTelemetry(sessionId, {
      fileName: `${file.name}.gpmf`,
      contentType: 'application/octet-stream',
      content: new Blob([gpmfTrack.rawData]),
    })

    const rawTelemetry = await parseGoProToRawTelemetry([gpmfTrack])
    const firstCoordinate = getFirstCoordinate(rawTelemetry)
    if (firstCoordinate == null) {
      throw new Error(
        'No GPS data found in the file. Make sure GPS was enabled on the camera and had a satellite lock during the recording.'
      )
    }

    const track = await api.trackApi.findTrack(firstCoordinate)
    const device: Device = { name: 'GoPro', version: '8' }

    await processAndCreateSession(
      rawTelemetry,
      sessionId,
      track,
      { hasLeanAngle: true, hasVideo: true },
      file.name,
      device
    )
    addAsVideo(sessionId, file)
  }

  return {
    extensions: ['.MP4', '.LRV'],
    keepsFileAsSource: false,
    processFile,
    addAsVideo,
    error: apiError,
  }
}
